import React from 'react';
import classnames from 'classnames';
import Button from '@material-ui/core/Button';
import {List} from 'immutable';
import {UserContextViewPlusButton} from 'components/triggers/UserContextView';
import FeedbackTrigger, {
  FeedbackMatchAllContext,
  FeedbackMatchAnyContext,
  FeedbackRecipientContext,
  FeedbackReviewerIsRecipient,
  FeedbackTriggerCoachContext,
  FeedbackTriggerCommentContext,
  FeedbackTriggerHighlightContext,
  FeedbackTriggerLibraryAccessContext,
  FeedbackTriggerListensContext,
  FeedbackTriggerScoreContext,
  FeedbackTriggerSharesContext,
  FeedbackTriggerType,
} from 'records/FeedbackTrigger';
import {
  CoachesContext,
  CommentContext,
  HighlightContext,
  LibraryContext,
  ListensContext,
  ScoresContext,
  SharesContext,
} from './rule-context';
import TriggerIconButton from 'components/triggers/TriggerIconButton';
import Popover from 'components/pure/utils/Popover';
import './FeedbackRuleWizard.styl';
import {UserContextType} from 'components/triggers/UserContextType';
import {OverlappingCommentsIcon} from '@components/icon/OverlappingCommentsIcon';
import {CommentIcon} from '@components/icon/CommentIcon';
import {ScorecardIcon} from '@components/icon/ScorecardIcon';
import {HighlightIcon} from '@components/icon/HighlightIcon';
import {UserHeadsetIcon} from '@components/icon/UserHeadsetIcon';
import {BookmarkIcon} from '@components/icon/BookmarkIcon';
import {ShareIcon} from '@components/icon/ShareIcon';

export default class FeedbackRuleWizard extends React.Component {
  constructor(props) {
    super(props);
    this.addUserContext = this.addUserContext.bind(this);
    this.removeUserContext = this.removeUserContext.bind(this);
    this.updateUserContext = this.updateUserContext.bind(this);

    this.optionClick = this.optionClick.bind(this);
    this.removeOption = this.removeOption.bind(this);
    this.renderEmbeddedContent = this.renderEmbeddedContent.bind(this);
    this.getRecipientsPath = this.getRecipientsPath.bind(this);
    this.getRecipients = this.getRecipients.bind(this);
    this.toRecipient = this.toRecipient.bind(this);
    this.addRecipient = this.addRecipient.bind(this);
    this.removeRecipient = this.removeRecipient.bind(this);
    this.updateRecipient = this.updateRecipient.bind(this);

    this.saveTriggerClick = this.saveTriggerClick.bind(this);
    this.detailsContextChange = this.detailsContextChange.bind(this);

    this.state = {
      rule: props.rule || new FeedbackTrigger(),
      saving: false,
      selectUserPopoutVisible: false,
      userContextSelected: false,
    };
  }

  detailsContextChange(context, index) {
    this.setState({
      rule: this.state.rule.setIn(['context', 'matchAll', index], context),
    });
  }

  addUserContext(selectedOption) {
    this.setState({
      rule: this.state.rule.update('user', user => user.push(selectedOption)),
    });
  }

  getUserContextPath(index) {
    return ['user', index];
  }

  removeUserContext(path) {
    this.setState({
      rule: this.state.rule.deleteIn(path),
    });
  }

  updateUserContext(path, context) {
    this.setState({
      rule: this.state.rule.setIn(path, context),
    });
  }

  recipientSelection(selectedOption) {
    const {recipientContext} = this.state;
    this.setState({
      recipientContext: recipientContext.push(selectedOption),
      recipientSelectionVisible: false,
    });
  }

  addRecipientContextClick() {
    this.setState({
      recipientSelectionVisible: true,
    });
  }

  removeRecipientContext(index) {
    const {recipientContext} = this.state;
    this.setState({
      recipientContext: recipientContext.delete(index),
    });
  }

  updateRecipientContext(i, context) {
    const {recipientContext} = this.state;
    this.setState({
      recipientContext: recipientContext.set(i, context),
    });
  }
  showFeedbackType() {
    this.setState({
      ...this.state,
      feedbackTypeVisible: true,
    });
  }

  feedbackOptionChange(feedbackType) {
    this.setState({
      feedbackType,
      ruleDetails: null,
    });
  }

  canSave() {
    return this.state.rule && this.state.rule.ready && this.state.rule.ready();
  }

  saveTriggerClick() {
    this.setState({saving: true});
    const {rule} = this.state;
    this.props.saveTriggerClick(rule);
  }

  matchAllContainsType(matchAll, feedbackType) {
    return matchAll.matchAll.some(item => item.type === feedbackType);
  }

  /**
   * Get the index of the condition that matchAny recipients, if index is specified get path into matchAny context
   * else return the path to the list of receipients, return null if matchAny does not exist
   * @param index optional index into path
   */
  getRecipientsPath(index = null) {
    const matchAll = this.state.rule.getIn(['context', 'matchAll', 0, 'matchAll']);
    const recipIndex = matchAll.findIndex(item => item.type === FeedbackTriggerType.MATCH_ANY);
    if (recipIndex < 0) {
      return null;
    }
    return index === null
      ? ['context', 'matchAll', 0, 'matchAll', recipIndex, 'matchAny']
      : ['context', 'matchAll', 0, 'matchAll', recipIndex, 'matchAny', index];
  }

  // convert to feedback recipient, embed in FeedbackRecipientContext if needed
  toRecipient(recipient) {
    if (recipient instanceof FeedbackReviewerIsRecipient) {
      return recipient;
    } else {
      return new FeedbackRecipientContext({
        recipient: recipient,
      });
    }
  }

  getRecipients() {
    const path = this.getRecipientsPath();
    return path ? this.state.rule.getIn(path) : null;
  }

  /**
   * adds a recipient
   * @param newRecipient the new recipient to add
   * @param modifiedRule modified rule to use, else use state.rule
   */
  addRecipient(newRecipient, modifiedRule = null) {
    if (!newRecipient.type) {
      console.log(`Warning: newRecipient type field is missing ${newRecipient}`);
      return;
    }
    const rule = modifiedRule !== null ? modifiedRule : this.state.rule;
    const path = this.getRecipientsPath();
    const recipientContext = this.toRecipient(newRecipient);
    const newRule =
      path !== null
        ? rule.updateIn(path, matchAny => matchAny.push(recipientContext))
        : rule.updateIn(['context', 'matchAll', 0, 'matchAll'], matchAll =>
            matchAll.push(new FeedbackMatchAnyContext({matchAny: [recipientContext]}))
          );
    this.setState({rule: newRule});
  }

  /**
   * removes recipient at path
   * @param path
   */
  removeRecipient(path) {
    const parent = path.slice(0, -1);
    const removeParent = this.state.rule.getIn(parent).size === 1;
    if (path) {
      this.setState({
        rule: this.state.rule.removeIn(removeParent ? parent.slice(0, -1) : path),
      });
    }
  }

  /**
   * updates recipient at path
   * @param path original path to recipient being updated
   * @param newRecipient the new recipient
   */
  updateRecipient(path, newRecipient) {
    if (!newRecipient.type) {
      console.log(`Warning: newRecipient type field is missing ${newRecipient}`);
      return;
    }
    const recipientContext = this.toRecipient(newRecipient);
    if (path) {
      const newRule = this.state.rule.setIn(path, recipientContext);
      this.setState({
        rule: newRule,
      });
    }
  }

  renderContext(matchAll, index) {
    // XXX EV-13652 may need from other merges
    const {userContextSelected} = this.state;
    const showContextButton = !userContextSelected;
    const recipients = this.getRecipients();
    // map from FeedbackRecipientContext to UserConditions
    const userConditions = recipients
      ? recipients.map(item =>
          item.type === FeedbackTriggerType.RECIPIENT ? item.recipient : item
        )
      : new List();
    const usersEmpty = userConditions.size === 0;
    if (this.matchAllContainsType(matchAll, FeedbackTriggerType.COMMENT)) {
      return (
        <CommentContext
          usersEmpty={usersEmpty}
          context={matchAll}
          contextChange={newContext => this.detailsContextChange(newContext, index)}
          removeContext={() => this.removeOption(index)}
          renderEmbeddedContent={this.renderEmbeddedContent}
          showContextButton={showContextButton}
        />
      );
    } else if (this.matchAllContainsType(matchAll, FeedbackTriggerType.SCORES)) {
      return (
        <ScoresContext
          usersEmpty={usersEmpty}
          context={matchAll}
          contextChange={newContext => this.detailsContextChange(newContext, index)}
          removeContext={() => this.removeOption(index)}
          renderEmbeddedContent={this.renderEmbeddedContent}
          showContextButton={showContextButton}
        />
      );
    } else if (this.matchAllContainsType(matchAll, FeedbackTriggerType.HIGHLIGHT)) {
      return (
        <HighlightContext
          usersEmpty={usersEmpty}
          context={matchAll}
          contextChange={newContext => this.detailsContextChange(newContext, index)}
          removeContext={() => this.removeOption(index)}
          renderEmbeddedContent={this.renderEmbeddedContent}
          showContextButton={showContextButton}
        />
      );
    } else if (this.matchAllContainsType(matchAll, FeedbackTriggerType.LISTENS)) {
      return (
        <ListensContext
          usersEmpty={usersEmpty}
          context={matchAll}
          contextChange={newContext => this.detailsContextChange(newContext, index)}
          removeContext={() => this.removeOption(index)}
          renderEmbeddedContent={this.renderEmbeddedContent}
          showContextButton={showContextButton}
        />
      );
    } else if (
      this.matchAllContainsType(matchAll, FeedbackTriggerType.ADDS_TO_LIBRARY) ||
      this.matchAllContainsType(matchAll, FeedbackTriggerType.LIBRARY_TAG) ||
      this.matchAllContainsType(matchAll, FeedbackTriggerType.LIBRARY_ACCESS)
    ) {
      return (
        <LibraryContext
          usersEmpty={usersEmpty}
          context={matchAll}
          contextChange={newContext => this.detailsContextChange(newContext, index)}
          removeContext={() => this.removeOption(index)}
          renderEmbeddedContent={this.renderEmbeddedContent}
          showContextButton={showContextButton}
        />
      );
    } else if (this.matchAllContainsType(matchAll, FeedbackTriggerType.SHARES)) {
      return (
        <SharesContext
          usersEmpty={usersEmpty}
          context={matchAll}
          contextChange={newContext => this.detailsContextChange(newContext, index)}
          removeContext={() => this.removeOption(index)}
          renderEmbeddedContent={this.renderEmbeddedContent}
          showContextButton={showContextButton}
        />
      );
    } else if (this.matchAllContainsType(matchAll, FeedbackTriggerType.COACHES)) {
      return (
        <CoachesContext
          context={matchAll}
          contextChange={newContext => this.detailsContextChange(newContext, index)}
          removeContext={() => this.removeOption(index)}
          renderEmbeddedContent={this.renderEmbeddedContent}
          showContextButton={showContextButton}
        />
      );
    } else {
      return null;
    }
  }

  optionClick(extraContext) {
    const contextItems = [extraContext];
    const newContext = new FeedbackMatchAllContext({
      matchAll: List(contextItems),
    });
    this.setState({
      rule: this.state.rule.updateIn(['context', 'matchAll'], matchAll =>
        matchAll.push(newContext)
      ),
    });
  }

  removeOption(index) {
    this.setState({
      rule: this.state.rule.removeIn(['context', 'matchAll', index]),
    });
  }

  getInitialOptions() {
    return (
      <div className="initial-options">
        <TriggerIconButton
          label="Coaches"
          onClick={() => this.optionClick(new FeedbackTriggerCoachContext())}>
          <OverlappingCommentsIcon />
        </TriggerIconButton>
        <TriggerIconButton
          label="Scores"
          onClick={() => this.optionClick(new FeedbackTriggerScoreContext())}>
          <ScorecardIcon />
        </TriggerIconButton>
        <TriggerIconButton
          label="Comments"
          onClick={() => this.optionClick(new FeedbackTriggerCommentContext())}>
          <CommentIcon />
        </TriggerIconButton>
        <TriggerIconButton
          label="Highlights"
          onClick={() => this.optionClick(new FeedbackTriggerHighlightContext())}>
          <HighlightIcon />
        </TriggerIconButton>
        <TriggerIconButton
          label="Listens"
          onClick={() => this.optionClick(new FeedbackTriggerListensContext())}>
          <UserHeadsetIcon />
        </TriggerIconButton>
        <TriggerIconButton
          label="Shares"
          onClick={() => this.optionClick(new FeedbackTriggerSharesContext())}>
          <ShareIcon />
        </TriggerIconButton>
        <TriggerIconButton
          label="Adds to Library"
          onClick={() => this.optionClick(new FeedbackTriggerLibraryAccessContext(), false)}>
          <BookmarkIcon />
        </TriggerIconButton>
      </div>
    );
  }

  renderInitialOptions() {
    return (
      <div className="rule-row empty-row">
        <label>
          <span>and</span>
        </label>
        <div className="rule-definition">
          <div className="call-option">
            <div className="rule-context">{this.getInitialOptions()}</div>
          </div>
        </div>
      </div>
    );
  }

  renderContinuingOptions() {
    return (
      <div className="rule-row empty-row">
        <label>
          <span>and</span>
        </label>
        <div className="rule-definition">
          <div className="call-option">
            <div className="rule-context" />
          </div>
        </div>
      </div>
    );
  }

  renderEmbeddedContent() {
    // XXX EV-13652 review function to remove get recipients
    const recipients = this.getRecipients();
    // convert from FeedbackRecipientContext to UserConditions and create a map of paths,recipients
    const recipientMap = recipients
      ? recipients.map((item, i) => [
          this.getRecipientsPath(i),
          item.type === FeedbackTriggerType.RECIPIENT ? item.recipient : item,
        ])
      : new List();
    return (
      <UserContextViewPlusButton
        key="userView"
        contextPathMap={recipientMap}
        buttonLabel="Optional: Specify the caller"
        contextType={UserContextType.CALL}
        addItem={this.addRecipient}
        removeItem={this.removeRecipient}
        updateItem={this.updateRecipient}
        cog={true}
        onSelect={userContextSelected => this.setState({userContextSelected})}
      />
    );
  }

  componentDidMount() {
    setTimeout(() => this.setState({selectUserPopoutVisible: true}), 0);
  }

  render() {
    const {rule, saving, selectUserPopoutVisible} = this.state;
    const contextPathMap = rule.user.map((context, i) => [this.getUserContextPath(i), context]);
    const actionConditions = rule.context ? rule.context.matchAll : List();
    const canSave = this.canSave() && !saving;

    return (
      <div className="create-feedback-rule">
        <p className="rule-title">What will trigger this rule?</p>
        <div>
          <div className="create-rule">
            <div className={classnames('rule-row', contextPathMap.size === 0 ? 'empty-row' : '')}>
              <label>When</label>
              <div className="rule-definition" ref={c => (this.userContextAnchor = c)}>
                <div className="call-option">
                  <div className="rule-context">
                    <UserContextViewPlusButton
                      contextPathMap={contextPathMap}
                      removeItem={this.removeUserContext}
                      updateItem={this.updateUserContext}
                      addItem={this.addUserContext}
                      onSelect={() => this.setState({selectUserPopoutVisible: false})}
                      buttonLabel="A User reviews a Call"
                      contextType={UserContextType.USER}
                    />
                  </div>
                  {selectUserPopoutVisible && (
                    <Popover
                      className={'destyle-popover'}
                      open={true}
                      canAutoPosition={false}
                      useLayerForClickAway={false}
                      anchorEl={this.userContextAnchor}
                      anchorOrigin={{
                        horizontal: 'right',
                        vertical: 'center',
                      }}
                      targetOrigin={{horizontal: 'left', vertical: 'center'}}>
                      <div className="sidebar-wrapper">
                        <div className="sidebar-container feedback-sidebar">
                          <div className="sidebar-contents">
                            <div className="tooltip-header">
                              You can select a user in this section
                            </div>
                            <div className="tooltip-content">
                              Click on the "<u>anybody</u>" link to change the user(s) whose
                              feedback will trigger this rule.
                            </div>
                          </div>
                        </div>
                      </div>
                    </Popover>
                  )}
                </div>
              </div>
            </div>
            {actionConditions.size === 0 && contextPathMap.size > 0 && this.renderInitialOptions()}
            {actionConditions.size > 0 &&
              actionConditions.map((contextItem, i) => (
                <div className="rule-row" key={i}>
                  <label>
                    <span>and</span>
                  </label>
                  <div className="rule-definition">
                    <div className="feedback-option">
                      <div className="rule-context">{this.renderContext(contextItem, i)}</div>
                    </div>
                  </div>
                </div>
              ))}
          </div>
          <div className="button-container">
            <Button
              variant="contained"
              color="primary"
              className="ev-button"
              disabled={!canSave}
              onClick={this.saveTriggerClick}>
              Next
            </Button>
            <Button variant="outlined" className="ev-button" onClick={this.props.cancelClick}>
              Cancel
            </Button>
          </div>
        </div>
      </div>
    );
  }
}
